import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../shared/utils/firebase';
import { formatDateSafe } from '../../../../shared/utils/dateHelpers';
import { LoadingSpinner } from '../../../../shared/components';

interface AlliedHealthAssessment {
  submission_timestamp?: unknown;
  discipline?: string;
  name?: string;
  summary_of_findings?: string;
  recommendations_for_training_block?: string;
}

interface AlliedHealthAssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
}

export const AlliedHealthAssessmentDialog: React.FC<AlliedHealthAssessmentDialogProps> = ({
  open,
  onClose,
  clientId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<AlliedHealthAssessment | null>(null);

  useEffect(() => {
    if (!open || !clientId) {
      return;
    }

    const fetchAssessment = async () => {
      setLoading(true);
      setError(null);
      setAssessment(null);

      try {
        const docRef = doc(db, 'allied_health_assessment', clientId);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          setAssessment(snapshot.data() as AlliedHealthAssessment);
        } else {
          setError('Allied health assessment data is not available for this client.');
        }
      } catch (err) {
        console.error('Failed to load allied health assessment data:', err);
        setError('Unable to load allied health assessment data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [open, clientId]);

  const renderValue = (value?: string) => {
    if (!value) {
      return 'N/A';
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : 'N/A';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Allied Health Assessment</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <LoadingSpinner minHeight="160px" />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : assessment ? (
          <Box display="flex" flexDirection="column" gap={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date Submitted
                </Typography>
                <Typography variant="body1">
                  {formatDateSafe(assessment.submission_timestamp, 'MMMM dd, yyyy • HH:mm', 'N/A')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Specialty
                </Typography>
                <Typography variant="body1">{renderValue(assessment.discipline)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Clinician
                </Typography>
                <Typography variant="body1">{renderValue(assessment.name)}</Typography>
              </Grid>
            </Grid>

            <Paper elevation={0} sx={{ p: 2, border: theme => `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Summary of Findings
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {renderValue(assessment.summary_of_findings)}
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, border: theme => `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Training Block Recommendations
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {renderValue(assessment.recommendations_for_training_block)}
              </Typography>
            </Paper>
          </Box>
        ) : (
          <Typography>No allied health assessment data found.</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlliedHealthAssessmentDialog;
